import { adminService } from "@/api/admin/admin.service";
import type { TGdprRequest } from "@/api/admin/admin.types";
import {
  DataGrid,
  type TColumnDef,
  type TDataGridQuery,
  type TDateRangeValue,
  type TFilterDef,
} from "@/components/data-grid";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Textarea } from "@/components/ui/Textarea";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/utils";
import { RefreshCw } from "lucide-react";
import { useMemo, useState } from "react";

const PAGE_SIZE = 20;

const STATUSES = ["pending", "in_progress", "completed", "rejected"] as const;
const REQUEST_TYPES = [
  "data_access",
  "rectification",
  "deletion",
  "portability",
  "anonymization",
] as const;

// Valid transitions mirror the backend guard.
const VALID_TRANSITIONS: Record<string, string[]> = {
  pending: ["in_progress", "completed", "rejected"],
  in_progress: ["completed", "rejected"],
  completed: [],
  rejected: [],
};

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "-";
  return new Date(iso).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" && value ? value : undefined;
}

export function GdprRequestsPage() {
  const { toast } = useToast();
  const [reloadTick, setReloadTick] = useState(0);

  const [selected, setSelected] = useState<TGdprRequest | null>(null);
  const [draftStatus, setDraftStatus] = useState<string>("");
  const [draftNotes, setDraftNotes] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);

  const fetcher = async (query: TDataGridQuery) => {
    const dateRange = (query.filters.created as TDateRangeValue | undefined) ?? {};
    const response = await adminService.getGdprRequests(
      {
        limit: query.limit,
        offset: query.offset,
        ...(asString(query.filters.status) ? { status: asString(query.filters.status) } : {}),
        ...(asString(query.filters.request_type)
          ? { request_type: asString(query.filters.request_type) }
          : {}),
        ...(asString(query.filters.user_id) ? { user_id: asString(query.filters.user_id) } : {}),
        ...(dateRange.from ? { date_from: dateRange.from } : {}),
        ...(dateRange.to ? { date_to: dateRange.to } : {}),
        ...(query.sort_by ? { sort_by: query.sort_by, sort_order: query.sort_order } : {}),
      },
      { signal: query.signal }
    );
    if (response.error) throw response.error;
    return response.data;
  };

  const columns: TColumnDef<TGdprRequest>[] = [
    { id: "request_type", header: "Type", type: "badge", sortable: true, sortKey: "request_type" },
    { id: "status", header: "Status", type: "status", sortable: true, sortKey: "status" },
    { id: "user_id", header: "User", type: "uuid" },
    {
      id: "resolution_notes",
      header: "Notes",
      type: "text",
      hideable: true,
      defaultHidden: true,
      accessor: (r) => r.resolution_notes ?? "",
    },
    { id: "requested_at", header: "Requested", type: "date", sortable: true, sortKey: "requested_at" },
    {
      id: "resolved_at",
      header: "Resolved",
      type: "date",
      sortable: true,
      sortKey: "resolved_at",
      accessor: (r) => r.resolved_at ?? null,
    },
  ];

  const filters: TFilterDef[] = [
    {
      id: "status",
      type: "select",
      label: "Status",
      options: STATUSES.map((s) => ({ label: s, value: s })),
    },
    {
      id: "request_type",
      type: "select",
      label: "Type",
      options: REQUEST_TYPES.map((t) => ({ label: t, value: t })),
    },
    { id: "user_id", type: "user", label: "User", placeholder: "Search by name or email…" },
    { id: "created", type: "dateRange", label: "Date" },
  ];

  const openDetail = (req: TGdprRequest) => {
    setSelected(req);
    setDraftStatus(req.status);
    setDraftNotes(req.resolution_notes ?? "");
  };

  const closeDetail = () => {
    setSelected(null);
    setDraftStatus("");
    setDraftNotes("");
  };

  const allowedNextStatuses = useMemo(() => {
    if (!selected) return [];
    return [selected.status, ...(VALID_TRANSITIONS[selected.status] ?? [])];
  }, [selected]);

  const handleSave = async () => {
    if (!selected) return;
    setIsSaving(true);
    const payload: { status?: string; resolution_notes?: string } = {};
    if (draftStatus !== selected.status) payload.status = draftStatus;
    if (draftNotes !== (selected.resolution_notes ?? "")) payload.resolution_notes = draftNotes;

    if (Object.keys(payload).length === 0) {
      setIsSaving(false);
      closeDetail();
      return;
    }

    const { data, error: err } = await adminService.updateGdprRequest(selected.id, payload);
    if (err) {
      toast({
        title: "Failed to update request",
        description: await extractErrorMessage(err),
        variant: "destructive",
      });
    } else if (data?.data) {
      toast({ title: "Request updated" });
      closeDetail();
      setReloadTick((t) => t + 1);
    }
    setIsSaving(false);
  };

  return (
    <div className="h-full overflow-y-auto bg-background p-4 sm:p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground sm:text-3xl">GDPR Requests</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Data-subject requests. Status changes are bookkeeping only and do not execute
              exports or deletions.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => setReloadTick((t) => t + 1)}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>

        <DataGrid<TGdprRequest>
          getRowId={(row) => row.id}
          persistKey="admin.gdpr"
          ariaLabel="GDPR requests"
          data={{ mode: "server", fetcher, deps: [reloadTick] }}
          columns={columns}
          filters={filters}
          pagination={{ mode: "offset", pageSize: PAGE_SIZE, pageSizeOptions: [10, 20, 50, 100] }}
          toolbar={{
            refresh: true,
            columnVisibility: true,
            density: true,
            export: ["csv", "json"],
          }}
          rowActions={(req) => [
            {
              label: "Update status",
              onClick: () => openDetail(req),
            },
          ]}
          emptyState={{ title: "No GDPR requests found." }}
        />
      </div>

      {/* Detail dialog */}
      <Dialog open={selected !== null} onOpenChange={(open) => !open && closeDetail()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manage GDPR Request</DialogTitle>
            <DialogDescription>
              Update the status and add resolution notes. This does not execute any data
              operation.
            </DialogDescription>
          </DialogHeader>

          {selected && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-2 rounded-md bg-muted p-3 text-xs">
                <span className="text-muted-foreground">Type</span>
                <span>{selected.request_type}</span>
                <span className="text-muted-foreground">User</span>
                <span className="font-mono">{selected.user_id}</span>
                <span className="text-muted-foreground">Requested</span>
                <span>{fmtDate(selected.requested_at)}</span>
                {selected.payload && Object.keys(selected.payload).length > 0 && (
                  <>
                    <span className="text-muted-foreground">Payload</span>
                    <span className="font-mono break-all">{JSON.stringify(selected.payload)}</span>
                  </>
                )}
              </div>

              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Status</label>
                <select
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm disabled:opacity-50"
                  value={draftStatus}
                  disabled={allowedNextStatuses.length <= 1}
                  onChange={(e) => setDraftStatus(e.target.value)}
                >
                  {allowedNextStatuses.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                {allowedNextStatuses.length <= 1 && (
                  <p className="text-xs text-muted-foreground">
                    This request is in a terminal state and cannot change status.
                  </p>
                )}
              </div>

              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">
                  Resolution notes
                </label>
                <Textarea
                  value={draftNotes}
                  onChange={(e) => setDraftNotes(e.target.value)}
                  placeholder="Optional notes about how this request was handled."
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={closeDetail}>
              Cancel
            </Button>
            <Button onClick={() => void handleSave()} disabled={isSaving}>
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
